import kebabCase from "lodash.kebabcase";
import { languageFromId } from "./utils/id-utils";
import { SanityGenericDownload } from "./model/common";
import { downloadUrl } from "./utils/cdn";

const slugOrFallback = (data: URLParts) => {
  const { slug, _id } = data;
  return slug ? slug.current : _id;
};

interface PathOptions {
  date?: boolean;
  year?: boolean;
  location?: boolean;
}

const pagePath = (
  data: URLParts,
  prefix: string | undefined,
  options?: PathOptions
) => {
  const slugPart = slugOrFallback(data);
  return fixedPath(data, prefix, slugPart, options);
};

const fixedPath = (
  data: URLParts,
  prefix: string | undefined,
  slugPart?: string,
  options?: PathOptions
) => {
  const { date, _id, location } = data;
  const parts = [""];
  // Safer from the ID as we have to explicitly query the language on a per-document-type basis.
  const language = languageFromId(_id);
  if (language && language !== "en") {
    parts.push(language.toLowerCase());
  }
  if (prefix) {
    parts.push(prefix);
  }
  if (date && options && options.date) {
    parts.push(date);
  } else if (date && options && options.year) {
    parts.push(date.split("-")[0]);
  }
  if (location && options && options.location) {
    parts.push(kebabCase(location.name.toLowerCase()));
  }
  if (slugPart) parts.push(slugPart);
  return parts.join("/");
};

/**
 * Parts of a document used to create URLs.
 */
export interface URLParts {
  _id: string;
  date?: string;
  _type: string;
  type?: string;
  slug?: {
    current: string;
  };
  location?: {
    name: string;
  };
}

export const defaultedLanguage = (document: any) => document.language || "en";

const impactPathPrefix = (pageName: string) => `about/impact/${pageName}`;

function urlForDocumentInternal(data: URLParts) {
  if (!data) {
    return "";
  }
  switch (data._type) {
    // News
    case "newsPage":
      return fixedPath(data, "news/");
    case "newsArticle":
      return pagePath(data, "news", { date: true });

    // About us
    case "aboutLandingPage":
      return fixedPath(data, "about/overview");
    case "aboutPage":
      return pagePath(data, "about");

    // Accessibility
    case "accessibilityLandingPage":
      return fixedPath(data, "accessibility");
    case "accessibilityPage":
      return pagePath(data, "accessibility");
    case "accessibilityActivity":
      return pagePath(data, "accessibility");

    // Impact (inside About us section)
    case "caseStudy":
      return pagePath(data, impactPathPrefix("case-studies"));
    case "caseStudiesPage":
      return fixedPath(data, impactPathPrefix("case-studies/"));
    case "foundationReport":
      if (data.type === "overview") {
        return fixedPath(
          data,
          impactPathPrefix("foundation-reports"),
          undefined,
          {
            location: true
          }
        );
      }
      return pagePath(data, impactPathPrefix("foundation-reports"), {
        location: true
      });
    case "foundationReportsPage":
      return fixedPath(data, impactPathPrefix("foundation-reports/"));
    case "researchPage":
      return fixedPath(data, impactPathPrefix("research/"));
    case "award":
      return pagePath(data, impactPathPrefix("awards"));
    case "awardsPage":
      return fixedPath(data, impactPathPrefix("awards/"));

    // do your :bit showcase
    case "dybProjectsLandingPage":
      return fixedPath(data, impactPathPrefix("do-your-bit/"));
    case "dybProject":
      return pagePath(data, impactPathPrefix("do-your-bit/projects"), {
        year: true
      });
    case "dybProjectListingPage":
      return fixedPath(data, impactPathPrefix("do-your-bit/projects/"));

    // Education
    case "projectsPage":
      return fixedPath(data, "projects/");
    case "projectCollection":
      return pagePath(data, "projects/collections/");

    case "makeListingPage":
      return fixedPath(data, "projects/make-it-code-it/");
    case "make":
      return pagePath(data, "projects/make-it-code-it");

    case "eduLessonsListingPage":
    case "unitOfWorkListingPage":
      return fixedPath(data, "teach/lessons/");
    case "unitOfWork":
      return pagePath(data, "teach/lessons/");
    case "lesson":
      return pagePath(data, "teach/lessons/");
    case "individualLesson":
      return pagePath(data, "teach/lessons/");

    case "eduPortalPage":
      return fixedPath(data, "teach/featured/");
    case "resourceListingPage":
      return fixedPath(data, "teach/classroom-resources/");
    case "resource":
      return pagePath(data, "teach/classroom-resources");
    case "eventListingPage":
      return fixedPath(data, "teach/events/");
    case "event":
      return pagePath(data, "teach/events/");

    case "teachPage":
      return pagePath(data, "teach");
    case "playgroundSurveyGuide":
      return pagePath(data, "teach/playground-survey/");

    case "product":
      return pagePath(data, "buy");
    case "productListingPage":
      return fixedPath(data, "buy/");
    case "buyPage":
      return fixedPath(data, "buy/where/");
    case "accessoriesPage":
      return fixedPath(data, "buy/accessories/");
    case "accessory":
      return pagePath(data, "buy/accessories");

    case "letsCodePage":
      return fixedPath(data, "code-ai/");

    case "featuresPage":
      return pagePath(data, "get-started/features");
    case "gettingStartedPage":
      return pagePath(data, "get-started/getting-started");
    case "userGuidePage":
      return pagePath(data, "get-started/user-guide");

    case "landingPage":
      return pagePath(data, undefined);
    case "genericPage":
      return pagePath(data, undefined);

    // DYB
    case "dybGenericPage":
      return pagePath(data, "teach/do-your-bit");
    case "dybWinners":
      return pagePath(data, "teach/do-your-bit/winners");
    case "globalGoalsResource":
      return pagePath(data, "teach/do-your-bit/global-goals");

    default:
      throw new Error(
        `Unknown type: ${
          data._type
        }. If you've added a new type of document, remember to add it to urls.ts.\n\nFull value passed:\n${JSON.stringify(
          data
        )}`
      );
  }
}

export function urlForDocument(data: URLParts) {
  // Special case where the link is external and the normalisation below doesn't apply:
  if (data._type === "pdCourse") {
    return `https://microbit.thinkific.com/courses/${data.slug?.current}`;
  }

  const url = urlForDocumentInternal(data).replace(/\/+/g, "/");
  const result = url.endsWith("/") ? url : `${url}/`;
  return result;
}

export const internalLinkUrl = (
  reference: URLParts | SanityGenericDownload | undefined,
  query: string | undefined,
  fragment: string | undefined,
  language: string | undefined,
  pathname: string | undefined = undefined
) => {
  // These are "internal", but we want the external Sanity CDN URL.
  if (reference && reference._type === "genericDownload") {
    const genericDownload = reference as SanityGenericDownload;
    // Select an alternative asset by language if one is available.
    const alternative =
      language && language !== "en"
        ? genericDownload.alternatives?.find(
            alterative => alterative.language === language
          )
        : undefined;
    const asset = alternative?.file?.asset ?? genericDownload.file.asset;
    return downloadUrl(asset, {
      overrideFilename: genericDownload.name,
      showInline: genericDownload.showInline
    });
  }
  const suffix = (query ? `?${query}` : "") + (fragment ? `#${fragment}` : "");
  if (reference) {
    const url = urlForDocument(reference as URLParts);
    const isAnchorLink = url.split("#")[0] === pathname?.split("#")[0];
    return `${isAnchorLink ? "" : url}${suffix}`;
  }
  return suffix;
};
